import React, { useCallback, useEffect, useRef, useState } from "react";
import LearnButton from "../../components/common/LearnButton";
import ButtonCard from "../../components/games/ButtonCard";
import FilledButton from "../../components/games/FilledButton";
import { LuRefreshCcw } from "react-icons/lu";
import { toast } from "react-toastify";
import beeUrl1 from "../../assets/beeC1.png";
import { useQueryClient } from "@tanstack/react-query";
import spellBeeCongratulationsUrl from "../../assets/spellBeeCongratulations.jpeg";
import axios from "axios";
import {
  decryptData,
  handleTransactionError,
  toastPromise,
} from "../../services/util";
import { useWallet } from "../../hooks/useWallet";
import { ethers } from "ethers";
import { parseEther } from "ethers/lib/utils";
import { getSignature } from "../../api/games";
import { useAuth } from "../../hooks/useAuth";
const basePositions = [
  { position: "top-0" },
  { position: "top-[54px] left-0" },
  { position: "top-[54px] right-0" },
  {
    position: "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2",
    isMarked: true,
  },
  { position: "bottom-[54px] left-0" },
  { position: "bottom-0 left-1/2 -translate-x-1/2" },
  { position: "bottom-[54px] right-0" },
];
const initialLetterList = [
  { char: "x", position: "top-0" },
  { char: "c", position: "top-[54px] left-0" },
  { char: "n", position: "top-[54px] right-0" },
  {
    char: "e",
    position: "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2",
    isMarked: true,
  },
  { char: "l", position: "bottom-[54px] left-0" },
  { char: "o", position: "bottom-0 left-1/2 -translate-x-1/2" },
  { char: "i", position: "bottom-[54px] right-0" },
];

const SpellBee = () => {
  const [dailyLeft, setDailyLeft] = useState(10);
  const [spellEarned, setSpellEarned] = useState(0);
  const [totalReward, setTotalReward] = useState(0);
  const [attemptsDone, setAttemptDone] = useState(0);
  const [spellWordList, setSpellWordList] = useState([]);
  const [letters, setLetters] = useState(initialLetterList);
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const [markedChar, setMarkedChar] = useState("");
  const [list, setList] = useState([]);
  const hasCalculated = useRef(false);
  const wallet = useWallet();
  const auth = useAuth();
  const [isGameStarted, setIsGameStarted] = useState(false);
  const [currentWord, setCurrentWord] = useState("");
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!isGameStarted) return;
    const timerId = setInterval(() => {
      setTimer((prevTimer) => {
        if (
          isSubmitting ||
          list?.length === spellWordList?.length ||
          prevTimer < 1
        ) {
          clearInterval(timerId);
          return prevTimer;
        } else {
          return prevTimer - 1;
        }
      });
    }, 1000);
    return () => clearInterval(timerId);
  }, [isGameStarted, isSubmitting, timer, list, spellWordList]);

  useEffect(() => {
    if (
      (timer === 0 || spellWordList?.length === list?.length) &&
      spellWordList?.length >= 1
    ) {
      if (!hasCalculated.current) {
        const timeTaken = timer;
        const currentPoints = handleCalculateEarning(
          list?.length,
          spellWordList?.length,
          timeTaken
        );
        setSpellEarned((prevSpellEarned) => {
          const totalCurrentEarned = prevSpellEarned + currentPoints;
          return totalCurrentEarned;
        });
        hasCalculated.current = true;
      }
    }
  }, [spellWordList, timer, list]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const pressedKey = event.key;
      if (!isGameStarted) return;
      if (pressedKey === "Enter") {
        event.preventDefault();
        handleConfirm();
        return;
      } else if (event.key === "Backspace") {
        handleDeleteLetter();
        return;
      } else if (letters?.map((letter) => letter?.char)?.includes(pressedKey)) {
        handleClickLetter(pressedKey.toLowerCase());
        return;
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [currentWord, isGameStarted]);

  const handleCalculateEarning = useCallback(
    (list, spelled, timeTaken) => {
      if (spelled >= 1) {
        if (list >= 7 && spelled >= 7 && timeTaken <= 60) {
          return 600;
        }
        const basePoints = 100;
        const additionalPoints = 400 * (spelled / list);
        const totalPoints = basePoints + additionalPoints;
        return Math.min(500, Math.floor(totalPoints));
      }
      return 0;
    },
    [spellWordList, list, timer, isSubmitting]
  );

  const handleStartGame = async () => {
    hasCalculated.current = false;
    if (dailyLeft < 1) return;
    setTimer(0);
    setDailyLeft((x) => x - 1);
    setAttemptDone((x) => x + 1);
    setSpellWordList([]);
    setCurrentWord("");
    setLoading(true);
    try {
      const data = await queryClient.fetchQuery({
        queryKey: ["get spell letters"],
        queryFn: () => axios.get("/api/game/bee"),
        staleTime: 0,
      });
      let mandatoryChar = await data?.mandatoryChar;
      let characters = await data?.characters;
      characters = characters.filter((char) => char !== mandatoryChar);
      const newLetterList = updateLettersList(characters, mandatoryChar);
      setLetters(newLetterList);
      setMarkedChar(mandatoryChar);
      const newCorrectWordList = await data?.encryptedWords;
      const decryptedWordList = newCorrectWordList?.map((word) => {
        return decryptData(word);
      });
      setList(decryptedWordList);
    } catch (e) {
      console.error(e.message);
      setLoading(false);
    } finally {
      setLoading(false);
    }
    setIsGameStarted(true);
    setTimer(120);
  };
  const handleConfirm = () => {
    if (currentWord === "") return;
    let text = "";
    if (currentWord.length < 3) {
      text = "Too short to be spelled!";
    } else if (!currentWord.includes(markedChar)) {
      text = "Centered letter missing!";
    } else if (!list.includes(currentWord)) {
      text = "Can't be spelled!";
    } else if (spellWordList.includes(currentWord)) {
      text = "Already spelled!";
    } else {
      text = "Amazing!";
      setSpellWordList((prev) => [...prev, currentWord]);
    }
    setMessage(text);
    setTimeout(() => {
      setMessage("");
      setCurrentWord("");
    }, 500);
  };
  const handleClickLetter = (letter) => {
    if (!timer) return;
    setCurrentWord((x) => x + letter);
  };
  const handleDeleteLetter = () => {
    if (currentWord === "") return;
    setCurrentWord((x) => x.slice(0, currentWord?.length - 1));
  };

  function updateLettersList(letters, markedChar) {
    const filteredLetters = letters.filter((char) => char !== markedChar);
    return basePositions.map((item, index) => ({
      char: index === 3 ? markedChar : filteredLetters.shift(),
      ...item,
    }));
  }
  const handleShuffle = () => {
    setLetters((prevLetters) => {
      const charsToShuffle = prevLetters
        .filter((letter) => !letter.isMarked)
        .map((letter) => letter.char);
      charsToShuffle.sort(() => Math.random() - 0.5);
      const newLetters = prevLetters.map((letter) =>
        letter.isMarked ? letter : { ...letter, char: charsToShuffle.pop() }
      );

      return newLetters;
    });
  };

  const handleSubmit = async () => {
    const multiplier = Number(localStorage.getItem("multiplier") || 1);
    let currentEarning = 0;
    if (timer === 0 || list?.length === spellWordList?.length) {
      currentEarning = spellEarned;
    } else {
      const currentPoints = handleCalculateEarning(
        list?.length,
        spellWordList?.length,
        timer
      );
      currentEarning = currentPoints + spellEarned;
      setSpellEarned(currentEarning);
    }
    setIsSubmitting(true);
    try {
      const config = wallet.getChainConfig();
      if (!config) return;
      const transactionOptions = {
        value: parseEther(
          String((config.features.gameValue * 2 * attemptsDone).toFixed(8))
        ),
        ...(config.features.gasLimit && { gasLimit: "900000" }),
      };
      const message = "Spell Bee";
      const nonce = 168;
      const signature = await getSignature({
        to: wallet.address,
        amount: ethers.utils.parseEther(currentEarning.toString()),
        message: message,
        nonce: nonce,
        chainId: wallet.chainId,
      });
      const [, tx] = await wallet.safeCallContract({
        name: "Game",
        method: "disperseToken",
        args: [
          "Bee",
          attemptsDone,
          multiplier,
          ethers.utils.parseEther(currentEarning.toString()),
          message,
          nonce,
          signature,
          transactionOptions,
        ],
      });

      if (tx) {
        await toastPromise(tx.wait(), {
          pending: "Submitting...",
          success: () => {
            setTotalReward((prevTotalReward) => {
              const currentTotalReward =
                +prevTotalReward + (currentEarning * multiplier);
              return currentTotalReward;
            });
            setAttemptDone(0);
            wallet.updatePoints({
              amount: +currentEarning * multiplier,
              chainId: wallet.chainId,
              walletAddress: wallet.address,
              moduleName: "bee",
              txHash: tx.hash,
            });
            auth.updateTokens();
            return "Submitted successfully!";
          },
          error: (err) => {
            handleTransactionError(err);
          },
        });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsSubmitting(false);
      setIsGameStarted(false);
      setSpellEarned(0);
      hasCalculated.current = false;
    }
  };
  const init = async () => {
    const [, left] = await wallet.safeCallContract({
      name: "Game",
      method: "getTotalTriesLeft",
      args: [wallet?.address, "Bee"],
    });
    if (left) {
      setDailyLeft(left?.toNumber());
    }
    const [, rewards] = await wallet.safeCallContract({
      name: "Game",
      method: "getTotalTokensEarned",
      args: [wallet?.address, "Bee"],
    });
    if (rewards) {
      setTotalReward(
        Number(ethers.utils.formatUnits(rewards.toString()).toString()).toFixed(
          0
        )
      );
    }
  };
  useEffect(() => {
    if (!wallet.address) return;
    init();
  }, [wallet.address, wallet.chainId]);
  return (
    <div
      className={`min-h-screen pb-5 xs:px-5 overflow-hidden 2xl:container w-full relative
         2xl:px-12  md:px-10 
      mx-auto md:pt-28 sm:pt-24 pt-20 font-redHat`}
    >
      <div className="flex flex-col items-start xl:pl-20 lg:pl-8 sm:gap-y-3 xs:gap-y-2 gap-y-1">
        <h1
          className={`xl:text-6xl lg:text-4xl   lg:w-fit w-full  relative xs:text-3xl text-2xl lg:text-left text-center font-bold font-akira `}
        >
          Spell Bee
        </h1>
        <p className="xl:text-2xl w-full md:text-xl sm:text-base text-[14px] lg:text-left text-center">
          Arrange the letters to spell the right words!
        </p>
      </div>
      <div className="flex lg:flex-row flex-col lg:gap-y-0 gap-y-5 mt-5 lg:items-start items-center w-full lg:justify-between">
        {isGameStarted ? (
          <>
            {list?.length === spellWordList?.length && (
              <div className="flex flex-col  h-full items-center w-1/2 ">
                <h1 className="mt-4 font-akira text-base font-bold flex flex-col items-center">
                  <span className="text-[#200052] lg:text-left text-center">
                    Congratulations!
                  </span>
                  <span className="lg:text-left text-center">
                    You spelled all the words.
                  </span>
                </h1>
                <div className="xl:h-96 h-72 xl:w-96 w-72 relative p-1 transition-all duration-250 ease hover:scale-[0.94] rounded-3xl mt-4 bg-gradient-to-br from-[#200052] to-[#000]">
                  <img
                    src={spellBeeCongratulationsUrl}
                    className=" rounded-3xl h-full w-full "
                    alt=""
                  />
                  <img
                    src={beeUrl1}
                    className="absolute z-20 top-0 xl:w-24 w-16 rotate-[-20deg] sm:-left-10 -left-6"
                    alt=""
                  />
                  <img
                    src={beeUrl1}
                    className="absolute z-20 bottom-0 xl:w-24 w-16 rotate-[-20deg] sm:-right-10 -right-6"
                    alt=""
                  />
                </div>
              </div>
            )}
            {timer === 0 && list?.length !== spellWordList?.length && (
              <div className="flex flex-col   h-full items-center w-1/2">
                <div
                  style={{
                    boxShadow: "0 0 10px rgba(255,255,255,0.7) inset",
                  }}
                  className="flex relative rounded-full px-20  py-3  border-2 border-primary bg-black font-bold font-redHat uppercase items-center gap-x-10"
                >
                  <span className="text-[13px]">time</span>
                  <h1 className=" text-2xl">
                    {Math.floor(timer / 60)
                      .toString()
                      .padStart(2, "0")}
                    :{(timer % 60).toString().padStart(2, "0")}
                  </h1>
                  <span className="block h-2 w-2 rounded-full bg-[#200052] absolute right-12 top-3" />
                </div>
                <h1 className="mt-12 font-akira lg:text-left text-center text-[#200052] font-bold">
                  Timer end
                </h1>
                <div className=" relative h-[320px] w-[320px] rounded-full flex justify-center items-center mt-5">
                  {letters?.map(({ char, position, isMarked }) => (
                    <LetterCard
                      key={char}
                      char={char}
                      isMarked={isMarked}
                      handleClick={() => handleClickLetter(char)}
                      className={`absolute ${position}`}
                    />
                  ))}
                </div>
              </div>
            )}
            {timer !== 0 && list?.length !== spellWordList?.length && (
              <SpellBeeGameWarpper
                timer={timer}
                handleConfirm={handleConfirm}
                currentWord={currentWord}
                handleClickLetter={handleClickLetter}
                handleDeleteLetter={handleDeleteLetter}
                handleShuffle={handleShuffle}
                letters={letters}
                markedLetter={markedChar}
                message={message}
              />
            )}
          </>
        ) : (
          <SpellBeeHomeIntro />
        )}
        {isGameStarted ? (
          <SpellBeeWordsListWrapper
            spelledWordList={spellWordList}
            list={list}
            dailyLeft={dailyLeft}
            handleSubmit={handleSubmit}
            isLoading={isSubmitting}
            isStarting={loading}
            handlePlayNext={handleStartGame}
            timer={timer}
            currentEarning={spellEarned}
            attemptsDone={attemptsDone}
          />
        ) : (
          <SpellBeeHomeOptions
            dailyLeft={dailyLeft}
            handleStartGame={handleStartGame}
            currentEarning={spellEarned}
            totalEarning={totalReward}
            loading={loading}
          />
        )}
      </div>
    </div>
  );
};

export default SpellBee;

const LetterCard = ({ char, className, handleClick, isMarked = false }) => {
  const letterCardRef = useRef(null);
  useEffect(() => {
    if (letterCardRef.current) {
      letterCardRef.current.focus();
    }
  }, []);
  return (
    <button
      ref={letterCardRef}
      tabIndex="0"
      role="button"
      onClick={handleClick}
      style={{
        clipPath:
          "polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)",
      }}
      className={`${className} uppercase transition-all duration-[300ms] hover:scale-[0.95] flex justify-center items-center w-[120px] h-[100px]  ${
        isMarked ? "bg-[#FFCE4B]" : "bg-[#DACFCF]"
      }`}
    >
      <h1 className="text-4xl font-bold text-[#000]">{char}</h1>
    </button>
  );
};

const SpellBeeRewardCard = ({ text, value }) => {
  return (
    <div
      className={` flex flex-col items-center
       sm:gap-y-5 gap-y-3
     `}
    >
      <div
        style={{
          boxShadow: "0 0 20px rgba(255,255,255,0.4) inset",
        }}
        className={`
            2xl:px-12 sm:px-8 xs:px-5 px-4 2xl:py-4 sm:py-2 xs:py-[6px] py-1 w-fit bg-black   rounded-full border-2 border-primary`}
      >
        <h1 className="font-redHat  uppercase 2xl:text-xl sm:text-base xs:text-[14px] text-[10px] text-center">
          {text}
        </h1>
      </div>
      <div
        className={` ${
          value < 99999
            ? "2xl:w-[320px] xl:w-72 sm:w-56 xs:w-52 w-36  2xl:text-5xl xl:text-4xl sm:text-3xl xs:text-2xl text-xl"
            : "2xl:text-5xl xl:text-4xl text-3xl px-10  w-fit"
        } border 2xl:py-20 xl:py-16 sm:py-[56px] xs:py-12 py-8 bg-black lg:rounded-[36px] md:rounded-[30px] rounded-3xl
         font-bold font-akira flex items-center justify-center`}
      >
        <h1>{value}</h1>
      </div>
    </div>
  );
};

const SpellBeeHomeIntro = () => {
  return (
    <div className="flex xl:pl-16 lg:pl-10 flex-col items-center lg:w-1/2 w-full">
      <div
        style={{
          boxShadow:
            "0 3px 12px -3px rgba(255,255,255,1) inset, 0 0 6px 0 #a9a9a9",
        }}
        className={`flex   font-redHat rounded-3xl bg-black  xl:py-8 py-6  justify-between items-center `}
      >
        <div className="flex border-r border-[#666666]  xl:w-60 xs:w-48 w-36 xs:gap-y-2 gap-y-1 flex-col   items-center">
          <p className="xl:text-xl sm:text-base xs:text-[14px] text-xs uppercase text-center  leading-[1.0] font-redHat font-bold">
            Word Length
          </p>
          <h1 className="text-[#200052]  xl:text-base sm:text-[13px] xs:text-xs text-[9px] font-medium">
            Length should be 4 or more
          </h1>
        </div>
        <div className="flex flex-col xs:gap-y-2 gap-y-1  xl:w-64 xs:w-52 w-36 text-center items-center justify-start ">
          <p className="xl:text-xl sm:text-base xs:text-[14px] text-xs uppercase  text-center  leading-[1.0] font-redHat font-bold">
            Word
          </p>
          <h1 className=" text-[#200052]  xl:text-base sm:text-[13px] xs:text-xs text-[9px] font-medium">
            Should contain centered letter
          </h1>
        </div>
      </div>

      <div className="w-[320px] relative h-[320px]  rounded-full flex justify-center items-center mt-8">
        <LetterCard char={"X"} className="absolute top-0" />
        <LetterCard char={"C"} className="absolute top-[54px] left-0" />
        <LetterCard char={"N"} className="absolute top-[54px] right-0" />
        <LetterCard
          isMarked={true}
          char={"E"}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        />
        <LetterCard char={"L"} className="absolute bottom-[54px] left-0" />
        <LetterCard
          char={"O"}
          className="absolute bottom-0 left-1/2 -translate-x-1/2"
        />
        <LetterCard char={"I"} className="absolute bottom-[54px] right-0" />
      </div>
      <p className="text-base text-[#B1B1B1] lg:w-96 sm:w-[500px] w-full xs:px-12 px-8 text-center mt-4">
        Decode the letters and spell the correct words and Earn Rewards
      </p>
    </div>
  );
};

const SpellBeeHomeOptions = ({
  dailyLeft,
  currentEarning,
  totalEarning,
  handleStartGame,
  loading,
}) => {
  return (
    <div className="flex flex-col sm:gap-y-5 gap-y-4 pb-4 items-center w-full">
      <LearnButton
        redirect={
          "https://medium.com/@blazpay/match-earn-repeat-conquer-the-memory-game-challenge-f4febec875a1"
        }
        title={"Learn to Play"}
        className={""}
      />
      <div className="flex xl:gap-x-6 gap-x-4">
        <SpellBeeRewardCard text="Current Rewards" value={currentEarning} />
        <SpellBeeRewardCard text="Total Rewards" value={totalEarning} />
      </div>
      <ButtonCard
        module="crypto-hunt"
        text={"Daily Left"}
        className={""}
        value={dailyLeft}
      />
      {dailyLeft > 0 ? (
        <FilledButton
          handleClick={handleStartGame}
          text={"Start"}
          isLoading={loading}
          loadintText={"Loading..."}
        />
      ) : dailyLeft === 0 && loading ? (
        <FilledButton handleClick={() => {}} text={"Starting..."} />
      ) : (
        <ButtonCard
          module="crypto-hunt"
          text={"You have no Attempts! Comeback tommorrow"}
        />
      )}
    </div>
  );
};
const SpellBeeGameWarpper = ({
  timer,
  className,
  handleShuffle,
  handleClickLetter,
  handleConfirm,
  handleDeleteLetter,
  letters,
  markedLetter,
  currentWord,
  message,
}) => {
  return (
    <div
      className={`${className} flex flex-col  h-full items-center lg:w-1/2 w-full`}
    >
      <div
        style={{ boxShadow: "0 0 10px rgba(255,255,255,0.7) inset" }}
        className="flex relative rounded-full sm:px-20 xs:px-16 px-12  sm:py-3 py-[10px]  border-2 border-primary bg-black font-bold font-redHat uppercase items-center gap-x-10"
      >
        <span className="xs:text-[13px] text-[11px]">time</span>
        <h1 className=" sm:text-2xl text-xl">
          {Math.floor(timer / 60)
            .toString()
            .padStart(2, "0")}
          :{(timer % 60).toString().padStart(2, "0")}
        </h1>
        <span className="block sm:h-2 h-[6px] sm:w-2 w-[6px] rounded-full bg-[#200052] absolute sm:right-16 right-8 sm:top-3 top-[10px]" />
      </div>
      <div className="flex flex-col w-full items-center">
        <div
          className={`${
            currentWord.length === 0 && "w-32 pt-4"
          } border-b-2  border-[#434343] pb-1 relative`}
        >
          <h1 className={`text-4xl  font-bold text-white mt-5 `}>
            {currentWord?.split("")?.map((letter, index) => {
              return (
                <span
                  className={`px-[3px] uppercase ${
                    letter === markedLetter && "text-[#ffce4b] uppercase"
                  }`}
                  key={index}
                >
                  {letter}
                </span>
              );
            })}
          </h1>
          <span
            style={{
              left: `${currentWord.length * 36}px`,
            }}
            className="absolute top-[30px] text-[#999999] text-[14px] w-40"
          >
            {message}
          </span>
        </div>

        <div className=" relative h-[320px] w-[320px] rounded-full flex justify-center items-center mt-5">
          {letters.map(({ char, position, isMarked }) => (
            <LetterCard
              key={char}
              char={char}
              isMarked={isMarked}
              handleClick={() => handleClickLetter(char)}
              className={`absolute ${position}`}
            />
          ))}
        </div>
        <div className="flex items-center justify-center sm:gap-x-5 ss:gap-x-3 gap-x-2 mt-4">
          <CustomButton handleClick={handleDeleteLetter} text={"Delete"} />
          <div
            onClick={handleShuffle}
            role="button"
            className="h-10 w-10 group transition-all duration-250 ease rounded-full bg-[#000] border-2 border-[#200052] flex items-center justify-center"
          >
            <LuRefreshCcw
              className={`sm:text-xl text-base group-active:rotate-180 transition-all duration-[420ms] ease-in-out `}
            />
          </div>
          <CustomButton handleClick={handleConfirm} text={"Confirm"} />
        </div>
      </div>
    </div>
  );
};

const SpellBeeWordsListWrapper = ({
  spelledWordList,
  dailyLeft,
  handleSubmit,
  isLoading,
  isStarting,
  timer,
  handlePlayNext,
  currentEarning,
  list,
  attemptsDone,
}) => {
  return (
    <div
      className={`lg:w-1/2 md:w-[60%] sm:w-[80%] xs:w-[86%] w-full xs:px-0 ss:px-10 px-5    ${
        (timer === 0 || list?.length === spelledWordList?.length) && "lg:-mt-8"
      } flex flex-col items-start`}
    >
      <h1 className="text-uppercase xl:text-3xl lg:text-2xl text-xl w-full lg:text-left text-center font-akira relative lg:left-4">
        Words Spelled
      </h1>
      <div className="2xl:pr-20 xl:pr-12 w-full flex flex-col items-center">
        <div className="mt-4 w-full border border-[#434343] sm:rounded-3xl rounded-2xl bg-[#000] 2xl:h-52 h-40 sm:pb-5 pb-3 sm:px-8 xs:px-6 px-4 sm:py-6 xs:py-5 py-4">
          <p className="text-[#696969] 2xl:text-[15px] text-xs">
            {spelledWordList?.length === list?.length ? (
              <>Congratulations! you spelled all the words</>
            ) : (
              <> You have spelled {spelledWordList?.length} words</>
            )}
          </p>
          <div className="w-full flex items-start gap-2 flex-wrap mt-2">
            {timer === 0 ? (
              <>
                {list?.map((word, index) => {
                  return (
                    <p
                      key={index}
                      className={`py-1 xl:px-6 px-5 uppercase rounded-lg ${
                        spelledWordList?.includes(word)
                          ? "bg-green-400"
                          : "bg-[#200052]"
                      } 2xl:text-xl xl:text-base text-[14px]`}
                    >
                      {word}
                    </p>
                  );
                })}
              </>
            ) : (
              <>
                {spelledWordList?.map((word, index) => {
                  return (
                    <p
                      key={index}
                      className="py-1 xl:px-6 px-5 uppercase rounded-lg bg-[#232323] 2xl:text-xl xl:text-base text-[14px]"
                    >
                      {word}
                    </p>
                  );
                })}
              </>
            )}
          </div>
        </div>
        <div className="flex items-center justify-center w-full xl:gap-x-6 sm:gap-x-4 gap-x-2  mt-4">
          <CustomCard
            text="Words Spelled"
            value={`${spelledWordList?.length}/${list?.length}`}
          />
          <CustomCard text="daily left" value={dailyLeft} />
        </div>
        {(attemptsDone > 1 ||
          timer === 0 ||
          list?.length === spelledWordList?.length) && (
          <div
            className={`mt-4 w-full lg:px-20 md:px-12 sm:px-10 flex flex-col items-center
       gap-y-2
     `}
          >
            <h1 className="font-akira text-[#200052]  uppercase 2xl:text-xl sm:text-base xs:text-[14px] text-[10px] text-center">
              Current Earning
            </h1>

            <div
              className={` ${
                currentEarning < 99999
                  ? "w-full  2xl:text-5xl xl:text-4xl sm:text-3xl xs:text-2xl text-xl"
                  : "2xl:text-5xl xl:text-4xl text-3xl px-10  w-full"
              } border 2xl:py-16  xs:py-12 py-8 bg-black lg:rounded-[36px] md:rounded-[30px] rounded-3xl
         font-bold font-akira flex items-center justify-center`}
            >
              <h1>{currentEarning}</h1>
            </div>
          </div>
        )}
        <div className="flex justify-center gap-x-4 items-center">
          {(timer === 0 || list?.length === spelledWordList?.length) &&
            dailyLeft > 0 && (
              <FilledButton
                text="Play Next"
                handleClick={handlePlayNext}
                isLoading={isStarting}
                loadintText={"Starting..."}
                className="mt-4"
              />
            )}
          <FilledButton
            text="Submit"
            handleClick={handleSubmit}
            isLoading={isLoading}
            loadintText={"Submitting..."}
            className="mt-4"
          />
        </div>
        <span className="block mt-2 text-[10px] text-[#999999] ">
          Submit before leaving the page
        </span>
      </div>
    </div>
  );
};

const CustomButton = ({ handleClick, className, text, isLoading }) => {
  return (
    <button
      style={{
        boxShadow:
          "0 6px 12px -8px #fff inset,0 -12px 15px 0px rgba(255,255,255,0.4) inset, 0 0 10px rgba(255,255,255,0.3) inset",
      }}
      onClick={handleClick}
      className={` ${className} rounded-full cursor-pointer 
        font-bold bg-radial-play text-white sm:px-10 px-8   sm:py-2 py-[11px] font-redHat  active:scale-[0.96]  sm:text-base text-xs h-fit transition duration-300 ease-in-out`}
      disabled={isLoading}
    >
      {text}
    </button>
  );
};

const CustomCard = ({ text, value }) => {
  return (
    <div
      style={{ boxShadow: "0 0 10px rgba(255,255,255,0.7) inset" }}
      className="flex items-center uppercase justify-center font-bold xl:gap-x-4 sm:gap-x-3 gap-x-2 xl:px-12 sm:px-8 ss:px-6 px-4 bg-[#000] xl:py-3 sm:py-2 ss:py-[6px] py-1 rounded-full border-2 border-[#200052]"
    >
      <span className="xl:text-base sm:text-[14px] ss:text-xs text-[10px]">
        {text}
      </span>
      <h1 className="xl:text-xl sm:text-base ss:text-[14px] text-xs font-bold text-[#200052]">
        {value}
      </h1>
    </div>
  );
};
